import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import NavigationService from '@/Management/NavigationService';
import ResourceManager from '@/Management/ResourceManager';
import Config from '@/Management/GlobalConfiguration';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import RADApplication from '@/Management/RADApplication';
import StorageHelper from '@/Utility/StorageHelper';

import ImageViewModelType from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import MenuViewModelType from '@/ViewModels/Controls/MenuViewModel';
import MenuItemViewModelType from '@/ViewModels/Controls/MenuItemViewModel';
import { ILanguageDTO } from "coreframetypings/dto";
import { IDropdownMenuItem, IUserDropdownMenuViewModelConstructorParameter } from '#/CoreFrameInterfaces';
import SwitchViewModelType from '@/Components/Switch/SwitchViewModel';
import GlobalConfiguration from "@/Management/GlobalConfiguration";

export default class UserDropdownMenuViewModel extends ControlViewModel {
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelType>('ImageViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelType>('LinkViewModel');
    private MenuViewModel = ComponentManager.getViewModel<typeof MenuViewModelType>('MenuViewModel');
    private MenuItemViewModel = ComponentManager.getViewModel<typeof MenuItemViewModelType>('MenuItemViewModel');
    private SwitchViewModel = ComponentManager.getViewModel<typeof SwitchViewModelType>('SwitchViewModel');

    /**
     * Gibt an, ob das Dark Theme angewendet werden soll
     */
    public darkModeActive: KnockoutObservable<boolean>;

    /**
     * Menu Eintr�ge
     */
    public dropdownMenuItems: KnockoutObservableArray<IDropdownMenuItem>;

    public constructor(params: IUserDropdownMenuViewModelConstructorParameter) {
        super(params);

        this.darkModeActive = Config.headerView.showDarkModeSwitch ?
            this.observableOrDefault(StorageHelper.bind('dark-mode', false)) :
            this.observableOrDefault(false);

        const menuItems = params.dropdownMenuItems ?? this.createDropdownMenuItems();
        RADApplication.getInstance().onBeforeLoadUserDropdownMenu(menuItems);
        this.dropdownMenuItems = this.observableOrDefault(menuItems) as KnockoutObservableArray<IDropdownMenuItem>;
    }

    private createDropdownMenuItems(): Array<IDropdownMenuItem> {
        let items: IDropdownMenuItem[] = [];

        //Stellt den aktuellen Benutzernamen dar
        items.push({
            uiGenerator: 'Link',
            itemViewModel: this.createUserNameLabelViewModel(),
            cssClass: 'user-info'
        });

        //Link zum "Passwort �ndern" Dialog
        if (this.showChangePassword())
            items.push({
                uiGenerator: 'Link',
                itemViewModel: this.createChangePasswordLinkViewModel(),
                cssClass: 'change-password'
            });

        //Link zum "2FA" Dialog
        if (RADApplication.getInstance().user.isTwoFASelfServiceEnabled())
            items.push({
                uiGenerator: 'Link',
                itemViewModel: this.createTwoFALinkViewModel(),
                cssClass: '2fa'
            });

        //Sprachauswahl Menu
        items.push({
            uiGenerator: 'Menu',
            itemViewModel: this.createLanguageMenuViewModel(),
            cssClass: 'change-lang'
        });

        //Switch zum Aktivieren DarkMode
        if (Config.headerView.showDarkModeSwitch)
            items.push({
                uiGenerator: 'Switch',
                itemViewModel: this.createDarkModeSwitchViewModel(),
                cssClass: 'darkmode-menu'
            });

        //Link zum Ein-/Ausblenden des SettingsMen�s
        items.push({
            uiGenerator: 'Link',
            itemViewModel: this.createShowSettingsLinkViewModel(),
            cssClass: 'settings'
        });

        //Link zum Abmelden
        items.push({
            uiGenerator: 'Link',
            itemViewModel: this.createLogoutLinkViewModel(),
            cssClass: 'user-logout'
        })

        return items;
    }

    /**
    * Erzeugt das LabelViewModel f�r den Usernamen
    */
    private createUserNameLabelViewModel(): LinkViewModelType {
        const locator = this.locator() + '.username';

        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: locator,
            label: new this.LabelViewModel({
                parentLocator: locator,
                locator: locator + '.label',
                caption: this.defineComputed(() => {
                    return RADApplication.getInstance().user.lastName() + ',\n ' + RADApplication.getInstance().user.firstName();
                })
            }),
            image: new this.ImageViewModel({
                source: 'UserDropdownMenu/user.svg',
                parentLocator: locator,
                locator: locator + '.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LOCALIZATION.LANGUAGE_USERS_ERGNAME'
                })
            })
        })
    }

    private createChangePasswordLinkViewModel(): LinkViewModelType {
        const locator = this.locator() + '.change-password';

        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: locator,
            label: new this.LabelViewModel({
                parentLocator: locator,
                locator: locator + '.label',
                ergName: 'MENU_CHANGE_PASSWORD_NAME'
            }),
            image: new this.ImageViewModel({
                source: 'UserDropdownMenu/keys.svg',
                locator: locator + '.link.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MENU_CHANGE_PASSWORD_NAME'
                })
            }),
            onClick: () => {
                NavigationService.getInstance().navigateToChangePassword();
            }
        })
    }

    private showChangePassword(): boolean {
        let showChangePassword = !RADApplication.getInstance().user.isSingleSignOn();
        // Ist es bereits ein SSO User ben�tigen wir keine weitere Pr�fung
        // Ist der User aber �ber LDAP ohne SSO darf es auch nicht angezeigt werden.
        if (showChangePassword) {
            if (RADApplication.getInstance().user.additionalLoginInformation() &&
                RADApplication.getInstance().user.additionalLoginInformation().IsExternAuthentification) {
                showChangePassword = RADApplication.getInstance().user.additionalLoginInformation().IsExternAuthentification == '1';
            }
        }

        return showChangePassword;
    }

    private createTwoFALinkViewModel(): LinkViewModelType {
        const locator = this.locator() + '.2fa';

        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: locator,
            label: new this.LabelViewModel({
                parentLocator: locator,
                locator: locator + '.label',
                ergName: 'MENU_2FA_NODE_NAME'
            }),
            image: new this.ImageViewModel({
                source: 'UserDropdownMenu/shield.svg',
                locator: locator + '.link.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MENU_2FA_NODE_NAME'
                })
            }),
            onClick: () => {
                NavigationService.getInstance().navigateToTwoFA();
            }
        })
    }

    private createLanguageMenuViewModel(): MenuViewModelType {
        const locator = this.locator() + '.change-language';

        const languageMenuItem = new this.MenuItemViewModel({
            parentLocator: locator,
            locator: locator + '.menuItem',
            link: new this.LinkViewModel({
                label: new this.LabelViewModel({
                    parentLocator: this.locator,
                    locator: locator + '.label',
                    ergName: 'MENU_LANGUAGE_NODE_NAME'
                }),
                image: new this.ImageViewModel({
                    source: 'UserDropdownMenu/environment.svg',
                    parentLocator: locator,
                    locator: locator + '.image',
                    alternativeText: new this.LabelViewModel({
                        ergName: 'MENU_LANGUAGE_NODE_NAME'
                    })
                })
            }),
            image: new this.ImageViewModel({
                source: 'UserDropdownMenu/navigate_right.svg',
                parentLocator: locator,
                locator: locator + '.image'
            }),
            items: this.getLanguageMenuItems(),
            isEnabled: this.defineComputed(() => {
                return RADApplication.getInstance().currentDetailViewContext() == null;
            })
        })

        return new this.MenuViewModel({
            direction: 'right',
            orientation: 'vertical',
            parentLocator: this.locator,
            locator: locator,
            isVisible: this.defineComputed(() => {
                return ResourceManager.getInstance().getLanguagesForLogin().length > 1;
            }),
            items: [languageMenuItem]
        })
    }

    private getLanguageMenuItems(): MenuItemViewModelType[] {
        const items = [];
        const locator = this.locator() + '.change-language.menuItem';

        let languages = ResourceManager.getInstance().getLanguagesForLogin() as ILanguageDTO[];
        for (let lang of languages) {
            const iso6391 = lang.iso6391;
            const imageVM = new this.ImageViewModel({
                source: 'UserDropdownMenu/language.svg',
                locator: lang.cultureInfoName + '.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.LOCALIZATION.RESOURCEITEM_LANGUAGE_ERGNAME'
                })
            });

            this.defineSubscription(imageVM.svgSource, (svgSource) => {
                imageVM.svgSource(this.changeTextInSvg(svgSource, iso6391));
            });

            items.push(new this.MenuItemViewModel({
                locator: locator + '.' + lang.cultureInfoName,
                link: new this.LinkViewModel({
                    label: new this.LabelViewModel({
                        locator: lang.cultureInfoName + '.link.label',
                        caption: lang.displayName
                    }),
                    image: imageVM,
                    onClick: () => {
                        if (iso6391 == ResourceManager.getInstance().language.value())
                            return;

                        this.increaseBusyCounter();
                        RADEnvironmentProxy.getInstance().changeLanguage({
                            lang: lang.iso6391
                        })
                            .then(() => {
                                ResourceManager.getInstance().language.value(iso6391)
                                NavigationService.getInstance().reload(false, false);
                            })
                            .always(() => {
                                this.decreaseBusyCounter();
                            });
                    }
                }),
                image: new this.ImageViewModel({
                    source: 'UserDropdownMenu/check.svg',
                    locator: 'current-language',
                    alternativeText: new this.LabelViewModel({
                        caption: iso6391
                    }),
                    isVisible: iso6391 == ResourceManager.getInstance().language.value()
                }),
                isEnabled: this.defineComputed(() => {
                    return iso6391 != ResourceManager.getInstance().language.value()
                })
            }))
        }

        return items;
    }

    private changeTextInSvg(svgString: string, newText: string): string {
        const xmlDoc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
        const textElement = xmlDoc.querySelector('text');

        if (!textElement) {
            return svgString;
        }

        textElement.textContent = newText;
        return new XMLSerializer().serializeToString(xmlDoc);
    }

    private createDarkModeSwitchViewModel(): SwitchViewModelType {
        const locator = this.locator() + '.dark-mode';

        return new this.SwitchViewModel({
            parentLocator: this.locator,
            locator: locator,
            image: new this.ImageViewModel({
                source: 'UserDropdownMenu/contrast.svg',
                parentLocator: locator,
                locator: 'image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MENU_DARK_MODE_TOOLTIP'
                })
            }),
            label: new this.LabelViewModel({
                parentLocator: locator,
                locator: locator + '.label',
                ergName: 'MENU_DARK_MODE_TOOLTIP'
            }),
            isChecked: this.darkModeActive
        })
    }

    /**
    * Erzeugt das LinkViewModel zum Aufruf der Einstellungen
     */
    private createShowSettingsLinkViewModel(): LinkViewModelType {
        const locator = this.locator() + '.settings';

        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: locator,
            label: new this.LabelViewModel({
                parentLocator: locator,
                locator: locator + '.label',
                ergName: 'OPTIONS_LANDSCAPE_TITLE'
            }),
            image: new this.ImageViewModel({
                source: GlobalConfiguration.inlineImages.GEARWHEELS,
                parentLocator: locator,
                locator: 'settingsImage',
                alternativeText: new this.LabelViewModel({
                    ergName: 'OPTIONS_LANDSCAPE_TITLE'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'OPTIONS_LANDSCAPE_TITLE_TOOLTIP'
            }),
            onClick: (openInNewWindow) => {
                NavigationService.getInstance().navigateToUrl('Settings', null, null, null, null, openInNewWindow);
            },
            hotkey: 'alt+o'
        });
    }

    /**
    * Erzeugt das LinkViewModel zum Aufrufen des Logouts
    */
    private createLogoutLinkViewModel(): LinkViewModelType {
        const locator = this.locator() + '.logout';

        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: locator,
            label: new this.LabelViewModel({
                parentLocator: locator,
                locator: locator + '.label',
                ergName: 'MENU_ITEM_LOGOUT'
            }),
            image: new this.ImageViewModel({
                source: 'UserDropdownMenu/standby.svg',
                locator: locator + '.link.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MENU_ITEM_LOGOUT_TOOLTIP'
                })
            }),
            onClick: () => {
                NavigationService.getInstance().loadViewFactory('Logout');
            }
        });
    }
}

ComponentManager.setViewModel('UserDropdownMenuViewModel', UserDropdownMenuViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');